import { RefreshCw } from 'lucide-react'
import type { CaptureCapabilities, CaptureVisibility } from '../types'

function hasDesktopCaptureOwner(capture?: Pick<CaptureVisibility, 'owner'> | null) {
  return Boolean(capture?.owner?.id?.trim() || capture?.owner?.device_id?.trim())
}

export function isDesktopCaptureDisconnected(capture?: Pick<CaptureVisibility, 'mode' | 'state' | 'connected' | 'owner'> | null) {
  if (!capture || capture.mode !== 'desktop') return false
  const state = String(capture.state)
  const active = state === 'running' || state === 'claimed' || state === 'live'
  // A newly requested desktop capture can briefly report a disconnected
  // relay before the local Mac claims it. It is still waiting to start; an
  // owner-scoped disconnect is the recoverable state that deserves a retry.
  return hasDesktopCaptureOwner(capture) && (state === 'disconnected' || (active && capture.connected === false))
}

export function isDesktopCaptureRetryable(capture?: Pick<CaptureVisibility, 'mode' | 'state' | 'connected' | 'owner'> | null) {
  return isDesktopCaptureDisconnected(capture) || (capture?.mode === 'desktop' && String(capture.state) === 'failed')
}

/** A producer may stop a requested or disconnected capture before its relay claims it. */
export function isDesktopCaptureActive(capture?: Pick<CaptureVisibility, 'mode' | 'state' | 'connected'> | null) {
  return capture?.mode === 'desktop' && ['starting', 'running', 'disconnected', 'stopping'].includes(String(capture.state))
}

/** A reconnect control is local to the matching connected desktop relay. */
export function canReconnectDesktopCapture(capture?: CaptureVisibility | null, capabilities?: CaptureCapabilities | null, currentUserId?: string | null) {
  if (!isDesktopCaptureRetryable(capture)) return false
  const desktop = capabilities?.desktop
  const ownerId = capture?.owner?.id?.trim()
  const ownerDeviceId = capture?.owner?.device_id?.trim()
  const localDeviceId = desktop?.device_id?.trim()
  const actorId = currentUserId?.trim()
  return Boolean(desktop?.available && desktop.connected && actorId && ownerId && actorId === ownerId && localDeviceId && ownerDeviceId && localDeviceId === ownerDeviceId)
}

function statusLabel(capture: CaptureVisibility, disconnected: boolean) {
  const state = String(capture.state)
  if ((state === 'disconnected' || state === 'starting') && !hasDesktopCaptureOwner(capture)) return state === 'disconnected' ? 'Waiting for this Mac' : 'Starting'
  if (disconnected) return 'Disconnected'
  if (state === 'starting') return 'Starting'
  if (state === 'stopping') return 'Stopping'
  if (state === 'completed' || state === 'complete') return 'Capture completed'
  if (state === 'failed' || state === 'expired') return 'Failed'
  return 'Connected'
}

export function CaptureStatus({ capture, onRetry, retrying = false }: { capture?: CaptureVisibility | null; onRetry?: () => void; retrying?: boolean }) {
  if (!capture || capture.mode !== 'desktop') return null
  const disconnected = isDesktopCaptureDisconnected(capture)
  const retryable = isDesktopCaptureRetryable(capture)
  const owner = capture.owner
  const ownerName = owner?.name?.trim()
  const deviceLabel = owner?.device_label?.trim()
  const identity = ownerName ? `Captured by ${ownerName}` : deviceLabel ? `Captured on ${deviceLabel}` : 'Desktop capture'
  const label = `${identity}${ownerName && deviceLabel ? ` · ${deviceLabel}` : ''} · ${statusLabel(capture, disconnected)}`
  return <div className={`capture-status${disconnected ? ' is-disconnected' : ''}`} role={disconnected ? 'status' : undefined} aria-label={label}>
    <span className="capture-status-indicator" aria-hidden="true" />
    <span className="capture-status-copy">{label}</span>
    {retryable && onRetry ? <button type="button" className="capture-status-retry" disabled={retrying} onClick={onRetry}>
      {retrying ? <RefreshCw size={12} className="spin" aria-hidden="true" /> : null}
      {retrying ? 'Reconnecting…' : 'Reconnect this Mac'}
    </button> : null}
  </div>
}
